<script lang="ts" setup>
import { ref } from 'vue';
import type ContextMenu from 'primevue/contextmenu';
import type { MenuItem } from 'primevue/menuitem';
import SaveFormDialog from '~/components/base/dialogs/SaveFormDialog.vue';
const props = defineProps<{
  items?: MenuItem[];
}>();
const { t } = useI18n();
// Correct ref typing for ContextMenu
const clientContextMenu = ref<InstanceType<typeof ContextMenu> | null>(null);

function toggle(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
  clientContextMenu.value?.toggle(event);
}

function alignSubmenu(event: MouseEvent) {
  const trigger = event.currentTarget as HTMLElement | null;
  const menuItem = trigger?.closest('.p-contextmenu-item') as HTMLElement | null;
  if (!menuItem) return;

  const submenu = menuItem.querySelector(
    ':scope > ul, :scope > .p-contextmenu-submenu',
  ) as HTMLElement | null;
  if (!submenu) return;

  const applyPosition = () => {
    const itemRect = menuItem.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxWidth = Math.min(200, Math.max(260, viewportWidth - 16));
    const maxHeight = Math.max(180, viewportHeight - itemRect.top - 12);

    submenu.style.setProperty('position', 'absolute', 'important');
    submenu.style.setProperty('top', '0px', 'important');
    submenu.style.setProperty('bottom', 'auto', 'important');
    submenu.style.setProperty('left', 'calc(100% - 2px)', 'important');
    submenu.style.setProperty('right', 'auto', 'important');
    submenu.style.setProperty('inset-block-start', '0px', 'important');
    submenu.style.setProperty('inset-block-end', 'auto', 'important');
    submenu.style.setProperty('inset-inline-start', 'calc(100% - 2px)', 'important');
    submenu.style.setProperty('inset-inline-end', 'auto', 'important');
    submenu.style.setProperty('transform', 'none', 'important');
    submenu.style.setProperty('box-sizing', 'border-box', 'important');
    submenu.style.setProperty('max-width', `${maxWidth}px`, 'important');
    submenu.style.setProperty('width', `min(${maxWidth}px, calc(100vw - 1rem))`, 'important');
    submenu.style.setProperty('max-height', `${maxHeight}px`, 'important');
    submenu.style.setProperty('overflow-y', 'auto', 'important');
    submenu.style.setProperty('overflow-x', 'hidden', 'important');

    let rect = submenu.getBoundingClientRect();
    if (rect.right > viewportWidth - 8) {
      submenu.style.setProperty('left', 'auto', 'important');
      submenu.style.setProperty('right', 'calc(100% - 2px)', 'important');
      submenu.style.setProperty('inset-inline-start', 'auto', 'important');
      submenu.style.setProperty('inset-inline-end', 'calc(100% - 2px)', 'important');
      rect = submenu.getBoundingClientRect();
    }

    if (rect.bottom > viewportHeight - 8) {
      const upwardOffset = Math.min(0, viewportHeight - 8 - rect.bottom);
      submenu.style.setProperty('top', `${upwardOffset}px`, 'important');
      submenu.style.setProperty('inset-block-start', `${upwardOffset}px`, 'important');
    }
  };

  applyPosition();
  requestAnimationFrame(applyPosition);
}
//Define values for the Delete/Restore dialog
const saveFormVisible = ref(false);
const pendingAction = ref<(() => unknown | Promise<unknown>) | null>(null);
const confirmType = ref();
const selectedEntity = ref<string | null>(null);
const entityName = ref<string>();

type MenuItemWithMeta = MenuItem & {
  entity?: string;
  name?: string;
  action?: () => unknown | Promise<unknown>;
};

//Opens the Dialog and waits user to confirm or close
const openDeleteDialog = (item: MenuItemWithMeta) => {
  const command =
    typeof item.command === 'function'
      ? () => item.command?.({ originalEvent: undefined as any, item })
      : null;
  pendingAction.value = command ?? item.action ?? null;
  saveFormVisible.value = true;
  selectedEntity.value = item.entity ?? null;
  entityName.value = item.name ?? t('common.contextMenu.element');
  confirmType.value = item.label;
}
//confirms the action from the dialog andexecutes the action
const confirmDelete = async () => {
  if (typeof pendingAction.value === 'function') {
    await pendingAction.value();
  }
  saveFormVisible.value = false;
  pendingAction.value = null;
}

const isCriticalAction = (item: MenuItem) =>
  actions.includes(String(item.label ?? ''));

//Array with critical actions, depends on language used the array changes and helps to trigger the dialog
const actions =[
  t('common.actions.delete'),
  t('common.contextMenu.factoryDefault'),
  t('common.contextMenu.reboot'),
  t('common.contextMenu.forceRegister'),
  t('common.contextMenu.factoryScreenDefaults'),
  t('common.contextMenu.shutdown'),
  t('common.contextMenu.standby'),
  t('common.contextMenu.updateCertificates'),
  t('common.contextMenu.wakeup'),
  t('common.contextMenu.configurationReset'),
]

defineExpose({ toggle });

</script>
<template>
  <div class="menu-button">
    <Button
      icon="pi pi-ellipsis-v"
      size="small"
      text
      @click="toggle"
    />
    <ContextMenu
      ref="clientContextMenu"
      :model="items"
      :autoZIndex="true"
      :baseZIndex="1000"
      :appendTo="'body'"
      class="stable-context-menu"
    >
      <template #item="{ item, props }">
        <a
          v-if="item.items"
          :href="item.url"
          v-bind="props.action"
          :target="item.target"
          class="menu-item-link"
          @mouseenter="alignSubmenu"
        >
          <span :class="item.icon" />
          <span class="menu-item-label ml-1">{{ item.label }}</span>
          <i class="pi pi-angle-right ml-auto" />
        </a>

        <a
          v-else-if="isCriticalAction(item)"
          :href="item.url"
          v-bind="props.action"
          :target="item.target"
          class="menu-item-link"
          @click.stop.prevent="openDeleteDialog(item)"
        >
          <span :class="item.icon" />
          <span class="menu-item-label ml-2">{{ item.label }}</span>
        </a>

        <a
          v-else
          :href="item.url"
          :target="item.target"
          v-bind="props.action"
          class="menu-item-link"
        >
          <span :class="item.icon" />
          <span class="menu-item-label ml-2">{{ item.label }}</span>
        </a>
      </template>
    </ContextMenu>
  </div>
  <!-- Opens dialog and handles delete action-->
  <SaveFormDialog
    v-model:visible="saveFormVisible"
    :confirmType="confirmType"
    :has-unsaved-form-input="false"
    :save-loading="false"
    :delete-loading="false"
    :entity-display-name="selectedEntity"
    :entity-name="entityName"
    :actions="actions"
    @delete="confirmDelete"
  />
</template>

<style scoped>
.menu-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Fix flicker when ContextMenu appears */
.stable-context-menu {
  background: hsl(var(--card)) !important;
  color: hsl(var(--foreground)) !important;
  will-change: transform, opacity;
  transition: opacity 0.15s ease, transform 0.15s ease;
  overflow: visible !important;
}
.p-contextmenu-enter-from,
.p-contextmenu-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
.p-contextmenu-enter-active,
.p-contextmenu-leave-active {
  transition: all 0.15s ease;
}

/* Optional: prevent layout shifts when menu opens */
.stable-context-menu .menu-item-link {
  display: flex;
  align-items: center;
  word-break: break-word;
  min-width: 160px;
  background: hsl(var(--card)) !important;
  color: hsl(var(--foreground)) !important;
}

.menu-item-link:hover {
  background-color: hsl(var(--accent)) !important;
}

/* Keep flyout submenus aligned with their triggering row */
.stable-context-menu :deep(.p-contextmenu-item) {
  position: relative;
}

.stable-context-menu :deep(.p-contextmenu-submenu) {
  top: 0 !important;
  left: calc(100% - 2px) !important;
  right: auto !important;
  bottom: auto !important;
  inset-block-start: 0 !important;
  inset-inline-start: calc(100% - 2px) !important;
  inset-inline-end: auto !important;
  inset-block-end: auto !important;
  margin: 0 !important;
  transform: none !important;
}
</style>
